import { getProductionCycleTime } from '@common/lib/getProductionCycleTime';
import {
  type BoostItemType,
  type UserFactory,
  type UserFactoryNextToBuild,
} from '@common/types/factoryType';
import { type PlantTypeText } from '@common/types/plant';
import { type BuildRequirementsType } from '@common/types/quarryType';
import { type RootState } from '@redux/store';
import { createSelector } from '@reduxjs/toolkit';

import { type CompateItemProps } from '@/common/types/components';
import { type ResourceTypeText } from '@/common/types/resource';

import {
  calculateTotalBoost,
  checkIfBoostTimersEnd,
  checkIfFreeBoostAvailable,
  createRequirementsList,
  generateActiveBoostList,
  getActiveBoost,
  getMaxExpiresTimeByPlant,
  mapBoostForCalcMining,
  selectAdsBoost,
  selectBoostX5,
  selectBoostX10,
  selectFreeBoost,
  selectPaidBoost,
  selectVipBoost,
} from './lib';

const selectFactories = (state: RootState): UserFactory[] | null =>
  state.factory.list;
const selectBoosts = (state: RootState): BoostItemType[] | null =>
  state.factory.boosts;

const selectFactoryNextToBuild = (
  state: RootState,
): UserFactoryNextToBuild | null => state.factory.nextToBuild;

const selectNextToBuildCreation = (
  state: RootState,
): BuildRequirementsType | undefined =>
  state.factory.nextToBuild?.creationRequirements;

const selectFactoryNextToBuildRequirements = createSelector(
  selectNextToBuildCreation,
  createRequirementsList,
);

const selectFactoriesAsObject = createSelector(selectFactories, (factories) => {
  if (factories) {
    const factoriesObject: Partial<Record<PlantTypeText, UserFactory>> = {};

    factories.forEach((quarry) => {
      factoriesObject[quarry.name] = quarry;
    });

    return factoriesObject;
  }
});

const selectFactoryByIdOrName = createSelector(
  selectFactories,
  (_: RootState, key: string | undefined): string | undefined => key,
  (factories, key) =>
    factories && key
      ? factories.find(({ id, name }) => id === key || name === key)
      : undefined,
);

const selectFactoryUpgradeData = createSelector(
  selectFactories,
  (_: RootState, id: string | undefined): string | undefined => id,
  (factories, id): CompateItemProps[] | undefined => {
    if (factories) {
      const factory = factories.find((factory) => factory.id === id);

      if (factory) {
        const nextLevel = factory.nextLevelRequirements;

        const production = {
          kind: 'production',
          now: factory.productionRateOriginal,
          next: nextLevel.productionRate,
        };

        const load = {
          kind: 'load',
          now: getProductionCycleTime(
            factory.storageCapacityOriginal,
            factory.productionRateOriginal,
          ),
          next: getProductionCycleTime(
            nextLevel.storageCapacity,
            nextLevel.productionRate,
          ),
        };

        return [production, load];
      }
    }
  },
);

const selectFactoryBoostList = createSelector(
  selectBoosts,
  generateActiveBoostList,
);

const selectFreeBoostForFactory = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectFreeBoost,
);

const selectAdsBoostForFactory = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectAdsBoost,
);

const selectVipBoostForFactory = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectVipBoost,
);

const selectPaidBoostForFactory = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectPaidBoost,
);

// const selectFactoryBoostsListById = createSelector(
//   selectBoosts,
//   (_: RootState, plantId?: string): string | undefined => plantId,
//   selectBoostList
// );
const selectBoostX5ForFactory = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectBoostX5,
);
const selectBoostX10ForFactory = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectBoostX10,
);

const selectTotalFactoryBoostById = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  calculateTotalBoost,
);

const getMaxExpiresTimeForFactoryBoosts = createSelector(
  selectBoosts,
  (_: RootState, plantId: string): string => plantId,
  getMaxExpiresTimeByPlant,
);

const selectBoostForFactory = createSelector(
  selectBoosts,
  (_: RootState, plantId: string | undefined): string | undefined => plantId,
  mapBoostForCalcMining,
);

const selectActiveBoostForFactory = createSelector(
  selectBoosts,
  getActiveBoost,
);

const boostFactoryTimersEnd = createSelector(
  selectBoosts,
  checkIfBoostTimersEnd,
);

const factoryFreeBoostAvailable = createSelector(
  selectBoosts,
  checkIfFreeBoostAvailable,
);
const selectMaterialsByResourceRecipe = createSelector(
  selectFactories,
  (
    _: RootState,
    resource: ResourceTypeText | undefined,
  ): ResourceTypeText | undefined => resource,
  (factories, resource) => {
    const result: { id: string; materialName: PlantTypeText }[] = [];

    if (factories && resource) {
      for (const factory of factories) {
        if (factory.source === resource) {
          result.push({ id: factory.id, materialName: factory.name });
        }
      }
    }

    return result;
  },
);

export {
  boostFactoryTimersEnd,
  factoryFreeBoostAvailable,
  getMaxExpiresTimeForFactoryBoosts,
  selectActiveBoostForFactory,
  selectAdsBoostForFactory,
  selectBoostForFactory,
  selectBoostX5ForFactory,
  selectBoostX10ForFactory,
  selectFactories,
  selectFactoriesAsObject,
  selectFactoryBoostList,
  selectFactoryByIdOrName,
  selectFactoryNextToBuild,
  selectFactoryNextToBuildRequirements,
  selectFactoryUpgradeData,
  selectFreeBoostForFactory,
  selectMaterialsByResourceRecipe,
  selectPaidBoostForFactory,
  selectTotalFactoryBoostById,
  selectVipBoostForFactory,
};
